// frontend/src/components/Automations/AutomationsManager.jsx
// Automations management surface (replaces the in-Settings AutomationsPanel).
//
// ROOT-CAUSE FIX: the old panel used raw fetch('/api/automations', {credentials:'include'})
// — no Authorization header, relative URL that breaks in production (Vercel ≠ DO origin).
// Backend requireAuth checks JWT Bearer; cookies are never set. The server returns a
// non-JSON error body; res.json() throws DOMException "The string did not match the
// expected pattern" (WebKit) with no friendly fallback.
//
// Fix: useAuth() → authFetch adds Bearer header; ${API_BASE} makes URL absolute;
// every res.json() has a .catch fallback; all date/grant renders are guarded.
import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext.jsx';
import { grantSummary, FLOOR_TYPES } from '../automationsGrantSummary.js';
import { describeSchedule } from './describeSchedule.js';
import { toTrigger, PICKER_SHAPES, DEFAULT_PICKER, DAYS, browserTimezone } from './schedulePicker.js';
import { suggestName } from './suggestName.js';
import './automations.css';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';
const ACTION_TYPES = ['send_email', 'create_draft', 'calendar_create_event'];

// Guard: returns a display string or null without throwing on bad/null timestamps.
function fmt(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? String(ts) : d.toLocaleString();
}

// Guarded like fmt(): a still-running row has no finished_at, and either
// timestamp can be null/garbage without throwing.
function runDuration(run) {
  if (!run?.started_at) return '';
  const start = new Date(run.started_at).getTime();
  const end = run.finished_at ? new Date(run.finished_at).getTime() : NaN;
  if (Number.isNaN(start) || Number.isNaN(end)) return run.finished_at ? '' : 'running…';
  const secs = Math.max(0, Math.round((end - start) / 1000));
  return secs < 60 ? `${secs}s` : `${Math.floor(secs / 60)}m ${secs % 60}s`;
}

export default function AutomationsManager({ initialAutomationId = null, onConsumeInitialAutomationId = null }) {
  const { authFetch } = useAuth();
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [runningId, setRunningId] = useState(null);   // automation currently executing
  const [openRunsFor, setOpenRunsFor] = useState(null); // automation whose history is open
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/api/automations`);
      const json = await res.json().catch(() => ({ success: false, error: 'Bad response from server' }));
      if (!res.ok || !json.success) throw new Error(json.error || `Failed to load (${res.status})`);
      setAutomations(Array.isArray(json.data) ? json.data : []);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const patch = useCallback(async (id, body) => {
    try {
      const res = await authFetch(`${API_BASE}/api/automations/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        // A3 (Group 6 PR A): the backend refuses to silently change what a
        // pre-authorized automation does — re-send `grant` (or `grant: null`
        // to revoke it) alongside `spec` to confirm the change. The raw code
        // means nothing to an operator, so translate it here, the one place
        // every PATCH in this component funnels through.
        const message = j.error === 'grant_reauth_required'
          ? 'This automation is pre-authorized. Re-confirm or revoke its grant before changing what it does.'
          : (j.error || `Update failed (${res.status})`);
        throw new Error(message);
      }
      load();
    } catch (e) { setError(e.message); }
  }, [authFetch, load]);

  const toggleEnabled = useCallback((a) => patch(a.id, { enabled: !a.enabled }), [patch]);

  const pauseAll = useCallback(async () => {
    try {
      await Promise.all(
        automations.filter((a) => a.enabled).map((a) =>
          authFetch(`${API_BASE}/api/automations/${a.id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ enabled: false }),
          })
        )
      );
      load();
    } catch (e) { setError(e.message); }
  }, [authFetch, automations, load]);

  // Fetch run history for one automation. Kept separate from `load` so opening
  // history never refetches the whole list.
  const loadRuns = useCallback(async (id) => {
    setRunsLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/api/automations/${id}/runs`);
      const json = await res.json().catch(() => ({ success: false, error: 'Bad response from server' }));
      if (!res.ok || !json.success) throw new Error(json.error || `Failed to load runs (${res.status})`);
      setRuns(Array.isArray(json.data) ? json.data : []);
      setError(null);
    } catch (e) {
      setError(e.message);
      setRuns([]);
    } finally {
      setRunsLoading(false);
    }
  }, [authFetch]);

  const toggleRuns = useCallback((a) => {
    setOpenRunsFor((cur) => {
      if (cur === a.id) return null;
      loadRuns(a.id);
      return a.id;
    });
  }, [loadRuns]);

  // Deep link (Group 5 PR A): a Discord notification's link lands on this
  // automation's run history. Consumed once the list is loaded, so the row
  // exists to open under; a stale id (a deleted automation) opens nothing and
  // is still consumed, so a later plain open does not re-fire it.
  useEffect(() => {
    if (!initialAutomationId || loading) return;
    if (automations.some((a) => a.id === initialAutomationId)) {
      setOpenRunsFor(initialAutomationId);
      loadRuns(initialAutomationId);
    }
    onConsumeInitialAutomationId?.();
  }, [initialAutomationId, loading, automations, loadRuns, onConsumeInitialAutomationId]);

  // The POST blocks for the WHOLE run (an analysis turn is ~15s), so without a
  // pending state the button looks dead and gets clicked again — each extra
  // click is another billed model turn with tool calls. `runningId` both shows
  // progress and makes the button idempotent for the duration of the run.
  const runNow = useCallback(async (a) => {
    if (runningId) return;
    setRunningId(a.id);
    setError(null);
    try {
      const res = await authFetch(`${API_BASE}/api/automations/${a.id}/run`, { method: 'POST' });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j.success === false) {
        if (res.status === 409 && j.error === 'run_in_progress') {
          setError('Already running — check back in a moment.');
          return;
        }
        throw new Error(j.error || `Run failed (${res.status})`);
      }
      // A run that returns status 'skipped' is not an error — it means the
      // engine kill switch is off, or the automation is disabled/expired.
      if (j.data?.status === 'skipped') {
        setError('Run skipped — automation disabled/expired, or AUTOMATIONS_ENABLED is not set on this service.');
      }
      // Open history on this automation so the result is visible immediately —
      // an analysis automation's entire product is its digest text.
      setOpenRunsFor(a.id);
      await Promise.all([load(), loadRuns(a.id)]);
    } catch (e) {
      setError(e.message);
    } finally {
      setRunningId(null);
    }
  }, [authFetch, load, loadRuns, runningId]);

  return (
    <div className="automations-manager">
      <div className="automations-toolbar">
        <span className="automations-count">
          {automations.length} automation{automations.length === 1 ? '' : 's'}
        </span>
        <button
          className="btn-secondary"
          onClick={pauseAll}
          disabled={!automations.some((a) => a.enabled)}
        >
          Pause all
        </button>
      </div>

      {loading && <p className="breathing-text">Loading automations…</p>}
      {error && <p className="error-text">{error}</p>}
      {!loading && !error && automations.length === 0 && (
        <p className="automations-empty">
          No automations yet. Ask Solomon in chat — e.g. &quot;every morning brief me on my top priorities.&quot;
        </p>
      )}

      <ul className="automations-list">
        {automations.map((a) => (
          <li key={a.id} className="glass-card automation-row">
            <div className="automation-row-head">
              <span className="automation-name">{a.name}</span>
              <span className={`badge status-${a.status}`}>{a.status}</span>
              <span className="automation-kind">{a.kind}</span>
            </div>
            <div className="automation-row-meta">
              <span className="meta-pill">&#x1F551; {describeSchedule(a.trigger_type, a.trigger_expr, a.timezone)}</span>
              <span className="meta-pill">&#x1F50C; MCP: Native</span>
            </div>
            <div className="automation-row-meta dim">
              <span>next: {fmt(a.next_run_at) || '—'}</span>
              <span>last: {fmt(a.last_run_at) || 'never'}</span>
            </div>
            {a.kind === 'action' && (
              <p className="grant-summary">{grantSummary(a.grant)}</p>
            )}
            {a.last_error && (
              <p className="error-text small">last error: {a.last_error}</p>
            )}
            <div className="automation-row-actions">
              <label className="toggle">
                <input type="checkbox" checked={!!a.enabled} onChange={() => toggleEnabled(a)} />
                <span>{a.enabled ? 'Enabled' : 'Disabled'}</span>
              </label>
              <button
                className={`btn-ghost${runningId === a.id ? ' is-running' : ''}`}
                onClick={() => runNow(a)}
                disabled={!!runningId}
                aria-busy={runningId === a.id}
              >
                {runningId === a.id ? 'Running…' : 'Run now'}
              </button>
              <button
                className="btn-ghost"
                onClick={() => toggleRuns(a)}
                aria-expanded={openRunsFor === a.id}
              >
                {openRunsFor === a.id ? 'Hide history' : 'History'}
              </button>
            </div>

            {openRunsFor === a.id && (
              <div className="automation-runs">
                {runsLoading && <p className="breathing-text">Loading runs…</p>}
                {!runsLoading && runs.length === 0 && (
                  <p className="automation-runs-empty">No runs yet.</p>
                )}
                {!runsLoading && runs.map((r) => (
                  <article key={r.id} className="automation-run">
                    <div className="automation-run-head">
                      <span className={`badge status-${r.status}`}>{r.status}</span>
                      <span className="automation-run-time">{fmt(r.started_at) || '—'}</span>
                      <span className="automation-run-time dim">{runDuration(r)}</span>
                      {a.kind === 'action' && (
                        <span className="automation-run-tally">
                          {r.actions_executed} executed / {r.actions_queued} queued
                          {r.actions_proposed ? ` / ${r.actions_proposed} proposed` : ''}
                        </span>
                      )}
                      {r.notify?.reason && (
                        <span className="automation-run-time dim" title={fmt(r.notify.at) || undefined}>Discord: {r.notify.reason}</span>
                      )}
                    </div>
                    {r.error && <p className="error-text small">{r.error}</p>}
                    {r.result_summary
                      ? <p className="automation-run-summary">{r.result_summary}</p>
                      : !r.error && <p className="automation-run-summary dim">No output recorded.</p>}
                  </article>
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>

      <AutomationForm authFetch={authFetch} onSaved={load} />
    </div>
  );
}

// Part A (2026-09-10): intent first, a human schedule picker instead of the
// trigger-type select + raw cron box, and the run-kind / grant machinery folded
// behind "Advanced". Frontend-only — the POST body shape is unchanged, the form
// now computes it (schedulePicker.js) instead of asking the operator to.
function AutomationForm({ authFetch, onSaved }) {
  const [prompt, setPrompt] = useState('');
  const [picker, setPicker] = useState(DEFAULT_PICKER);
  const [name, setName] = useState('');
  const [kind, setKind] = useState('analysis');
  const [actionType, setActionType] = useState('send_email');
  const [granted, setGranted] = useState(false);
  const [maxPerRun, setMaxPerRun] = useState(20);
  const [maxPerDay, setMaxPerDay] = useState(50);
  const [dryRun, setDryRun] = useState(3);
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);

  const trigger = toTrigger(picker);
  const effectiveName = name.trim() || (kind === 'analysis' ? suggestName(prompt) : `${actionType} on new leads`);
  const intentOk = kind === 'action' || prompt.trim().length > 0;
  const canSubmit = Boolean(trigger) && intentOk && effectiveName.length > 0 && !saving;

  const setPick = (patch) => setPicker((p) => ({ ...p, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    const body = { name: effectiveName, kind, ...trigger };
    const tz = browserTimezone();
    if (tz) body.timezone = tz;
    if (kind === 'analysis') {
      body.spec = { prompt: prompt.trim() };
    } else {
      body.spec = { action_type: actionType, target: { state: 'new' }, payload_template: {} };
      if (granted) {
        body.grant = {
          action_types: [actionType],
          max_per_run: Number(maxPerRun),
          max_per_day: Number(maxPerDay),
          dry_run_until_runs: Number(dryRun),
        };
      }
    }
    setSaving(true);
    try {
      const res = await authFetch(`${API_BASE}/api/automations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({ success: false, error: 'Bad response' }));
      if (!res.ok || !json.success) {
        setSaveError(json.error || `Create failed (${res.status})`);
        return;
      }
      setSaveError(null);
      setPrompt('');
      setName('');
      setPicker(DEFAULT_PICKER);
      onSaved();
    } catch (err) { setSaveError(err.message); }
    finally { setSaving(false); }
  };

  const previewGrant = {
    action_types: [actionType],
    max_per_run: Number(maxPerRun),
    max_per_day: Number(maxPerDay),
    dry_run_until_runs: Number(dryRun),
  };

  return (
    <form className="glass-card automation-form" onSubmit={submit}>
      <h3 className="playfair">New automation</h3>
      <p className="form-hint">Tip: you can also just tell Solomon in chat what to schedule.</p>

      {kind === 'analysis' ? (
        <>
          <label className="field">
            <span className="field-label">What should Solomon do each time?</span>
            <textarea
              className="glass-input"
              placeholder="e.g. Summarize any new leads and flag the ones worth a call"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={3}
            />
          </label>
          <p className="form-hint form-hint--tight">
            Researching a topic over time instead? That lives in Research (Capabilities → Research).
          </p>
        </>
      ) : (
        // An action automation runs a fixed outbound step — there is no prompt
        // to send, so the intent field is hidden (its text is kept, and comes
        // back if the kind is switched back to analysis) rather than silently
        // dropped from the request. Review round 1, finding 1.
        <p className="form-hint">
          Action automations run a fixed outbound step on new leads; there is no standing prompt.
          {prompt.trim() ? ' Your intent text is kept and will be used if you switch back to analysis.' : ''}
        </p>
      )}

      <div className="field">
        <span className="field-label">When</span>
        <div className="form-row schedule-picker">
          <select
            className="glass-input"
            aria-label="Schedule shape"
            value={picker.shape}
            onChange={(e) => setPick({ shape: e.target.value })}
          >
            {PICKER_SHAPES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          {picker.shape === 'weekly' && (
            <select
              className="glass-input"
              aria-label="Weekday"
              value={picker.dow}
              onChange={(e) => setPick({ dow: e.target.value })}
            >
              {DAYS.map((d, i) => <option key={d} value={String(i)}>{d}</option>)}
            </select>
          )}
          {(picker.shape === 'daily' || picker.shape === 'weekly') && (
            <input
              type="time"
              className="glass-input"
              aria-label="Time of day"
              value={picker.time}
              onChange={(e) => setPick({ time: e.target.value })}
            />
          )}
          {picker.shape === 'every_hours' && (
            <input
              type="number"
              className="glass-input schedule-hours"
              aria-label="Hours between runs"
              min={1}
              max={168}
              value={picker.hours}
              onChange={(e) => setPick({ hours: e.target.value })}
            />
          )}
          {picker.shape === 'once' && (
            <input
              type="datetime-local"
              className="glass-input"
              aria-label="Run once at"
              value={picker.at}
              onChange={(e) => setPick({ at: e.target.value })}
            />
          )}
        </div>
        <p className="schedule-preview">
          {trigger
            ? describeSchedule(trigger.trigger_type, trigger.trigger_expr)
            : 'Pick a time to see the schedule.'}
        </p>
      </div>

      <label className="field">
        <span className="field-label">Name <span className="field-optional">optional</span></span>
        <input
          className="glass-input"
          placeholder={effectiveName || 'Solomon will name it from your intent'}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </label>

      <details className="advanced">
        <summary>Advanced — run kind</summary>
        <label className="field">
          <span className="field-label">Kind</span>
          <select className="glass-input" value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="analysis">analysis (read-only)</option>
            <option value="action">action (gated outbound)</option>
          </select>
        </label>

        {kind === 'action' && (
          <fieldset className="grant-editor">
            <select className="glass-input" aria-label="Action type" value={actionType} onChange={(e) => setActionType(e.target.value)}>
              {ACTION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              {[...FLOOR_TYPES].map((t) => (
                <option key={t} value={t} disabled>{t} (always human)</option>
              ))}
            </select>
            <label className="toggle">
              <input type="checkbox" checked={granted} onChange={() => setGranted(!granted)} />
              <span>Pre-authorize (auto-execute within bounds)</span>
            </label>
            {granted && (
              <div className="grant-bounds">
                <label>
                  max/run
                  <input type="number" value={maxPerRun} onChange={(e) => setMaxPerRun(e.target.value)} />
                </label>
                <label>
                  max/day
                  <input type="number" value={maxPerDay} onChange={(e) => setMaxPerDay(e.target.value)} />
                </label>
                <label>
                  dry-run runs
                  <input type="number" value={dryRun} onChange={(e) => setDryRun(e.target.value)} />
                </label>
                <p className="grant-summary">{grantSummary(previewGrant)}</p>
              </div>
            )}
          </fieldset>
        )}
      </details>

      {saveError && <p className="error-text">{saveError}</p>}
      <button className="btn-primary" type="submit" disabled={!canSubmit}>
        {saving ? 'Creating…' : 'Create'}
      </button>
    </form>
  );
}
